import React from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  alpha,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  assignQuestionnaire,
  fetchConditionalQuestionnaireSections,
  fetchCoreQuestionnaireSections,
  fetchCustomQuestionnaireSections,
  type AssignQuestionnairePayload,
} from '@/lib/api/questionnaires';
import { getCurrentUser } from '@/lib/api/auth';
import { getClients } from '@/lib/api/clients';
import { useDebounce } from '@/hooks/useDebounce';
import type { Client } from '@/types/client';
import type { JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { ActionTour, type ActionTourStep } from '@/components/tutorial/ActionTour';
import notebookIcon from '@/assets/images/notebook-new.svg';
import clockCircleIcon from '@/assets/images/clock-circle-new.svg';
import usersGroupRoundedIcon from '@/assets/images/users-group-rounded.svg';
import verifiedCheckIcon from '@/assets/images/verified-check.svg';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { formatDelimitedDisplayLabel, formatDisplayLabel } from '@/utils/display-labels';

interface AssignmentClientRow {
  id: string;
  fullName: string;
  conditionsText: string;
  activityLabel: string;
}

interface AssignmentRowState {
  selected: boolean;
  deadline: string;
  message: string;
  methods: {
    email: boolean;
  };
}

const DEFAULT_ROW_STATE: AssignmentRowState = {
  selected: false,
  deadline: '',
  message: '',
  methods: {
    email: false,
  },
};

const ACTIVE_SECTION_COLOR = '#28b3d5';
const ACTIVE_SECTION_ROW_BG = alpha(ACTIVE_SECTION_COLOR, 0.12);
const ACTIVE_SECTION_ICON_BG = '#dff5fb';
const ACTIVE_SECTION_ICON_COLOR = '#1699c8';
const CLIENTS_PAGE_SIZE = 10;
const selectedDeliveryTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: 0,
  color: '#99A1AF',
} as const;
const tableHeaderCellSx = {
  borderBottomColor: '#edf2f7',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.6px',
  color: '#0A0A0A',
} as const;

const questionnaireAssignmentActionTourSteps: ActionTourStep[] = [
  {
    id: 'questionnaire-assignment-search',
    title: 'Search Clients',
    description: 'Use search to find the client you want to send this questionnaire to before selecting rows.',
  },
  {
    id: 'questionnaire-assignment-select',
    title: 'Select Clients',
    description: 'Select one or more clients. The final Assign button stays disabled until at least one client is selected.',
  },
  {
    id: 'questionnaire-assignment-deadline',
    title: 'Deadline',
    description: 'Add an optional due date per selected client. Dates in the past are blocked before sending.',
  },
  {
    id: 'questionnaire-assignment-message',
    title: 'Personal Message',
    description: 'Add an optional note that travels with the questionnaire assignment for that client.',
  },
  {
    id: 'questionnaire-assignment-delivery',
    title: 'Delivery Method',
    description: 'Choose Email when the system should send the questionnaire link to the selected client.',
  },
  {
    id: 'questionnaire-assignment-submit',
    title: 'Assign Questionnaire',
    description: 'After selecting clients and delivery options, this sends the questionnaire and makes it available on the responses page.',
  },
];

const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '26px', md: '32px' },
  lineHeight: { xs: '115%', md: '100%' },
  letterSpacing: 0,
  color: '#1e293b',
} as const;

function getQuestionnaireQuestionCount(section: JsonQuestionnaireSection): number {
  const customCount =
    typeof section.customQuestionsCount === 'number' && Number.isFinite(section.customQuestionsCount)
      ? section.customQuestionsCount
      : 0;

  if (typeof section.templateQuestionsCount === 'number' && Number.isFinite(section.templateQuestionsCount)) {
    return section.templateQuestionsCount + customCount;
  }

  return section.questions?.length ?? 0;
}

function getDurationLabel(section: JsonQuestionnaireSection): string {
  if (typeof section.estimatedTimeMinutes === 'number' && section.estimatedTimeMinutes > 0) {
    return `${section.estimatedTimeMinutes} min`;
  }

  const questionCount = getQuestionnaireQuestionCount(section);
  if (questionCount >= 40) return '10-15 min';
  if (questionCount >= 20) return '5-10 min';
  return '3-5 min';
}

function getAssignedClientsCount(section: JsonQuestionnaireSection): number {
  return typeof section.assignedClientsCount === 'number' && Number.isFinite(section.assignedClientsCount)
    ? section.assignedClientsCount
    : 0;
}

function getResponsesCount(section: JsonQuestionnaireSection): number {
  return typeof section.responsesCount === 'number' && Number.isFinite(section.responsesCount)
    ? section.responsesCount
    : 0;
}

function formatDateLabel(value?: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getConditionSummary(client: Client): string {
  const conditionNames = (client.medicalConditionDetails ?? [])
    .map((condition) => formatDisplayLabel(condition.conditionName))
    .filter((value): value is string => Boolean(value));

  if (conditionNames.length > 0) {
    return Array.from(new Set(conditionNames)).slice(0, 2).join(', ');
  }

  if (typeof client.medicalConditions === 'string' && client.medicalConditions.trim().length > 0) {
    return formatDelimitedDisplayLabel(client.medicalConditions);
  }

  return '';
}

function mapClientRow(client: Client): AssignmentClientRow {
  const formattedDate =
    formatDateLabel(client.lastInteractionAt) ??
    formatDateLabel(client.updatedAt) ??
    formatDateLabel(client.createdAt);

  return {
    id: client.id.toString(),
    fullName: client.fullName?.trim() || `${client.firstName} ${client.lastName}`.trim() || client.email,
    conditionsText: getConditionSummary(client),
    activityLabel: formattedDate ? `Last active: ${formattedDate}` : 'No recent activity',
  };
}

function SummaryMetric({
  icon,
  value,
  label,
  iconAlt,
}: {
  icon: string;
  value: string | number;
  label: string;
  iconAlt: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="img" src={icon} alt={iconAlt} sx={{ width: 16, height: 16, display: 'block' }} />
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          color: '#667085',
        }}
      >
        {value} {label}
      </Typography>
    </Box>
  );
}

const QuestionnaireAssignmentPage: React.FC = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = getCurrentUser();
  const nutritionistId = user?.id;
  const [searchQuery, setSearchQuery] = React.useState('');
  const [rowState, setRowState] = React.useState<Record<string, AssignmentRowState>>({});
  const debouncedSearch = useDebounce(searchQuery, 300);
  const minimumDeadlineDate = React.useMemo(() => toDateInputValue(new Date()), []);

  const {
    data: section,
    isLoading: isQuestionnaireLoading,
    isError: isQuestionnaireError,
    error: questionnaireError,
  } = useQuery({
    queryKey: ['questionnaire-assignment', questionnaireId, nutritionistId],
    queryFn: async () => {
      const [coreResult, customResult, conditionalResult] = await Promise.allSettled([
        fetchCoreQuestionnaireSections(nutritionistId),
        fetchCustomQuestionnaireSections(nutritionistId),
        fetchConditionalQuestionnaireSections(nutritionistId),
      ]);

      if (
        coreResult.status === 'rejected' &&
        customResult.status === 'rejected' &&
        conditionalResult.status === 'rejected'
      ) {
        throw coreResult.reason ?? customResult.reason ?? conditionalResult.reason ?? new Error('Failed to load questionnaire');
      }

      const coreSections = coreResult.status === 'fulfilled' ? coreResult.value.questionnaires : [];
      const customSections = customResult.status === 'fulfilled' ? customResult.value.questionnaires : [];
      const conditionalSections = conditionalResult.status === 'fulfilled' ? conditionalResult.value.questionnaires : [];
      const matchedSection = [...coreSections, ...customSections, ...conditionalSections].find(
        (entry) => entry.id === questionnaireId || entry.questionnaireId === questionnaireId
      );

      if (!matchedSection) {
        throw new Error('Questionnaire not found');
      }

      return matchedSection;
    },
    enabled: Boolean(questionnaireId && nutritionistId),
    // These 3 list-fetch calls are expensive. Cache for 5 minutes so navigating
    // away and back (or background invalidation) doesn't re-fire them.
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: clientsPages,
    isLoading: isClientsLoading,
    isError: isClientsError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch: refetchClients,
    isFetching: isClientsFetching,
  } = useInfiniteQuery({
    queryKey: ['questionnaire-assignment-clients', nutritionistId, debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const search = debouncedSearch.trim();
      const page = typeof pageParam === 'number' ? pageParam : 1;

      return getClients({
        limit: CLIENTS_PAGE_SIZE,
        page,
        status: 'active',
        search: search || undefined,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage.pagination?.page ?? 1;
      const totalPages = lastPage.pagination?.pages ?? 0;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: Boolean(nutritionistId),
  });

  const clients = React.useMemo(
    () => clientsPages?.pages.flatMap((page) => page.clients ?? []) ?? [],
    [clientsPages]
  );
  const clientsTotal = React.useMemo(() => {
    const firstPage = clientsPages?.pages[0];
    return firstPage?.pagination?.total ?? clients.length;
  }, [clients.length, clientsPages]);

  const visibleRows = React.useMemo(() => clients.map(mapClientRow), [clients]);
  const clientsLoadFailed = isClientsError && clients.length === 0;
  const hasActiveSearch = debouncedSearch.trim().length > 0;

  const assignMutation = useMutation({
    mutationFn: async (payload: AssignQuestionnairePayload) => {
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is required');
      }

      return assignQuestionnaire(questionnaireId, payload);
    },
    onSuccess: (_, payload) => {
      const assignedCount = payload.clients.length;

      setRowState((previous) => {
        const next = { ...previous };

        payload.clients.forEach(({ clientId }) => {
          if (next[clientId]) {
            next[clientId] = { ...DEFAULT_ROW_STATE, methods: { ...DEFAULT_ROW_STATE.methods } };
          }
        });

        return next;
      });

      showSuccessToast(
        'Questionnaire assigned',
        `Assigned to ${assignedCount} client${assignedCount === 1 ? '' : 's'}.`
      );

      if (questionnaireId) {
        navigate(`/questionnaires/${questionnaireId}/responses`);

        // Only invalidate the two queries the Responses page actually uses.
        // Avoid invalidating the assignment-page query keys here — the 3 list
        // fetches (core / custom / conditional) are now cached for 5 min and
        // the assignment page is being unmounted, so there is no active
        // subscriber to benefit from a refetch.
        void Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: ['questionnaire-responses', questionnaireId, nutritionistId] }),
          queryClient.invalidateQueries({ queryKey: ['questionnaire-responses-by-id', questionnaireId] }),
        ]);
      }
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to assign questionnaire');
    },
  });

  React.useEffect(() => {
    if (visibleRows.length === 0) return;

    setRowState((previous) => {
      let changed = false;
      const next = { ...previous };

      visibleRows.forEach((row) => {
        if (!next[row.id]) {
          next[row.id] = { ...DEFAULT_ROW_STATE, methods: { ...DEFAULT_ROW_STATE.methods } };
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [visibleRows]);

  const selectedCount = React.useMemo(
    () => visibleRows.filter((row) => rowState[row.id]?.selected).length,
    [rowState, visibleRows]
  );

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => rowState[row.id]?.selected);
  const someVisibleSelected = visibleRows.some((row) => rowState[row.id]?.selected);

  const updateRowState = React.useCallback(
    (rowId: string, updater: (current: AssignmentRowState) => AssignmentRowState) => {
      setRowState((previous) => {
        const current = previous[rowId] ?? { ...DEFAULT_ROW_STATE, methods: { ...DEFAULT_ROW_STATE.methods } };
        return {
          ...previous,
          [rowId]: updater(current),
        };
      });
    },
    []
  );

  const toggleSelectAll = React.useCallback(
    (checked: boolean) => {
      setRowState((previous) => {
        const next = { ...previous };
        visibleRows.forEach((row) => {
          const current = next[row.id] ?? { ...DEFAULT_ROW_STATE, methods: { ...DEFAULT_ROW_STATE.methods } };
          next[row.id] = {
            ...current,
            selected: checked,
          };
        });
        return next;
      });
    },
    [visibleRows]
  );

  const handleAssign = React.useCallback(() => {
    if (assignMutation.isPending) return;
    if (!questionnaireId || !section) return;
    if (clientsLoadFailed) return;

    const selectedRows = visibleRows.filter((row) => rowState[row.id]?.selected);
    if (selectedRows.length === 0) return;

    for (const selectedRow of selectedRows) {
      const deadline = rowState[selectedRow.id]?.deadline?.trim();
      if (deadline && deadline < minimumDeadlineDate) {
        showWarningToast('Invalid deadline', `Deadline for ${selectedRow.fullName} cannot be in the past.`);
        return;
      }
    }

    const payload = {
      clients: selectedRows.map((row) => {
        const currentState = rowState[row.id];
        const clientAssignment: AssignQuestionnairePayload['clients'][number] = {
          clientId: row.id,
        };

        const deadline = currentState?.deadline?.trim();
        if (deadline) {
          clientAssignment.deadline = deadline;
        }

        const personalMessage = currentState?.message?.trim();
        if (personalMessage) {
          clientAssignment.personalMessage = personalMessage;
        }

        clientAssignment.sendEmail = currentState?.methods?.email ?? false;

        return clientAssignment;
      }),
    };

    assignMutation.mutate(payload);
  }, [
    assignMutation,
    clientsLoadFailed,
    minimumDeadlineDate,
    questionnaireId,
    rowState,
    section,
    visibleRows,
  ]);

  if (isQuestionnaireLoading) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            overflow: 'hidden',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Skeleton variant="rounded" height={34} width={280} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={154} sx={{ mt: 3, borderRadius: '24px' }} />
          <Skeleton variant="rounded" height={560} sx={{ mt: 3, borderRadius: '24px' }} />
        </Paper>
      </Box>
    );
  }

  if (isQuestionnaireError || !section) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '26px', color: '#0f172a' }}>
            Questionnaire not available
          </Typography>
          <Typography sx={{ mt: 1.5, fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#667085' }}>
            {(questionnaireError as Error | undefined)?.message ?? 'The assignment screen could not be loaded.'}
          </Typography>
          <Button
            component={RouterLink}
            to="/questionnaires"
            variant="contained"
            sx={{
              mt: 3,
              borderRadius: '14px',
              bgcolor: '#25a8da',
              fontFamily: 'Inter, sans-serif',
              '&:hover': { bgcolor: '#1498ca' },
            }}
          >
            Back to Questionnaires
          </Button>
        </Paper>
      </Box>
    );
  }

  const questionCount = getQuestionnaireQuestionCount(section);
  const assignedClientsCount = getAssignedClientsCount(section);
  const responsesCount = getResponsesCount(section);
  const durationLabel = getDurationLabel(section);
  const footerMessage = clientsLoadFailed
    ? 'Clients must load successfully before you can assign this questionnaire.'
    : selectedCount > 0
      ? `${selectedCount} client${selectedCount === 1 ? '' : 's'} selected. Review each delivery setup before assigning.`
      : 'Select at least one client to continue';

  return (
    <Box data-demo-id="questionnaire-assignment-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 2, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to="/questionnaires"
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
              >
                Questionnaires
              </Link>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 4, md: 5 }, pt: 4 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '22px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fff',
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
            }}
          >
            <Typography sx={{ ...pageTitleSx, overflowWrap: 'anywhere' }}>{section.title}</Typography>
            <Typography
              sx={{
                mt: 1.25,
                fontFamily: 'Arial, sans-serif',
                fontSize: '16px',
                lineHeight: '24px',
                color: '#667085',
              }}
            >
              Manage and assign questionnaire to your clients
            </Typography>

            <Stack direction="row" flexWrap="wrap" gap={2.5} sx={{ mt: 2 }}>
              <SummaryMetric icon={notebookIcon} iconAlt="Questions" value={questionCount} label="questions" />
              <SummaryMetric icon={clockCircleIcon} iconAlt="Duration" value={durationLabel} label="" />
              <SummaryMetric icon={usersGroupRoundedIcon} iconAlt="Assigned" value={assignedClientsCount} label="assigned" />
              <SummaryMetric icon={verifiedCheckIcon} iconAlt="Responses" value={responsesCount} label="responses" />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              overflow: 'hidden',
              borderRadius: '22px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'stretch', lg: 'center' },
                gap: 2,
                borderBottom: '1px solid #edf2f7',
              }}
            >
              <TextField
                data-action-tour-id="questionnaire-assignment-search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search clients..."
                size="small"
                sx={{
                  width: { xs: '100%', lg: 332 },
                  maxWidth: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: 48,
                    borderRadius: '14px',
                    bgcolor: '#fbfdff',
                    '& fieldset': { borderColor: '#dbe6eb' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: '#98a2b3' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography
                sx={{
                  flexShrink: 0,
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '15px',
                  lineHeight: '22px',
                  color: '#98a2b3',
                  alignSelf: { xs: 'flex-start', lg: 'center' },
                  overflowWrap: 'anywhere',
                }}
              >
                Set a unique deadline, message, and delivery method per client.
              </Typography>
            </Box>

            {clientsLoadFailed && (
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.5,
                  borderBottom: '1px solid #edf2f7',
                  bgcolor: alpha('#f04438', 0.06),
                }}
              >
                <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#b42318' }}>
                  Couldn’t load clients. Retry to continue assignment.
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table sx={{ minWidth: { xs: 900, md: 1040, lg: 1120 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ width: { xs: 52, md: 64 }, borderBottomColor: '#edf2f7' }}>
                      <Checkbox
                        data-action-tour-id="questionnaire-assignment-select"
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && someVisibleSelected}
                        onChange={(event) => toggleSelectAll(event.target.checked)}
                        disabled={clientsLoadFailed || visibleRows.length === 0}
                        inputProps={{ 'aria-label': 'Select all clients' }}
                      />
                    </TableCell>
                    <TableCell sx={tableHeaderCellSx}>Client</TableCell>
                    <TableCell sx={{ ...tableHeaderCellSx, width: { xs: 160, md: 190 } }}>
                      Deadline
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderCellSx, width: { xs: 240, md: 330 } }}>
                      Personal Message (Optional)
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderCellSx, width: { xs: 180, md: 230 } }}>
                      Delivery Method
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isClientsLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={`loading-row-${index}`}>
                        <TableCell sx={{ borderBottomColor: '#edf2f7' }}>
                          <Skeleton variant="rounded" width={24} height={24} />
                        </TableCell>
                        <TableCell sx={{ borderBottomColor: '#edf2f7' }}>
                          <Skeleton variant="rounded" width={240} height={54} />
                        </TableCell>
                        <TableCell sx={{ borderBottomColor: '#edf2f7' }}>
                          <Skeleton variant="rounded" width={140} height={36} />
                        </TableCell>
                        <TableCell sx={{ borderBottomColor: '#edf2f7' }}>
                          <Skeleton variant="rounded" width={240} height={36} />
                        </TableCell>
                        <TableCell sx={{ borderBottomColor: '#edf2f7' }}>
                          <Skeleton variant="rounded" width={160} height={54} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : clientsLoadFailed ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 8, borderBottomColor: '#edf2f7', textAlign: 'center' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '20px',
                            color: '#0f172a',
                          }}
                        >
                          Unable to load clients
                        </Typography>
                        <Typography sx={{ mt: 1, fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085' }}>
                          Check your connection and try again. Assignment is unavailable until clients load.
                        </Typography>
                        <Button
                          variant="contained"
                          disabled={isClientsFetching}
                          onClick={() => {
                            void refetchClients();
                          }}
                          sx={{
                            mt: 2.5,
                            minWidth: 140,
                            height: 42,
                            borderRadius: '12px',
                            bgcolor: '#20AEDC',
                            color: '#fff',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#199cc7', boxShadow: 'none' },
                          }}
                        >
                          {isClientsFetching ? 'Retrying…' : 'Retry'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 8, borderBottomColor: '#edf2f7', textAlign: 'center' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '20px',
                            color: '#0f172a',
                          }}
                        >
                          {hasActiveSearch ? 'No clients match this search' : 'No active clients yet'}
                        </Typography>
                        <Typography sx={{ mt: 1, fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085' }}>
                          {hasActiveSearch
                            ? 'Try another search term or clear the search to see your clients.'
                            : 'Add an active client before assigning this questionnaire.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row) => {
                      const currentState =
                        rowState[row.id] ?? { ...DEFAULT_ROW_STATE, methods: { ...DEFAULT_ROW_STATE.methods } };
                      const isRowEnabled = currentState.selected;

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            opacity: isRowEnabled ? 1 : 0.68,
                            bgcolor: isRowEnabled ? ACTIVE_SECTION_ROW_BG : '#fff',
                            '&.MuiTableRow-hover:hover': {
                              bgcolor: isRowEnabled ? ACTIVE_SECTION_ROW_BG : '#fff',
                            },
                            '&:last-of-type td': { borderBottom: 'none' },
                            '& td': { borderBottomColor: '#edf2f7', verticalAlign: 'top' },
                          }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={currentState.selected}
                              onChange={(event) =>
                                updateRowState(row.id, (draft) => ({
                                  ...draft,
                                  selected: event.target.checked,
                                }))
                              }
                              sx={{
                                color: isRowEnabled ? ACTIVE_SECTION_ICON_COLOR : undefined,
                                '&.Mui-checked': {
                                  color: ACTIVE_SECTION_ICON_COLOR,
                                },
                              }}
                              inputProps={{ 'aria-label': `Select ${row.fullName}` }}
                            />
                          </TableCell>

                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 38,
                                  height: 38,
                                  bgcolor: isRowEnabled ? ACTIVE_SECTION_ICON_BG : '#e8f1ff',
                                  color: isRowEnabled ? ACTIVE_SECTION_ICON_COLOR : '#3b82f6',
                                }}
                              >
                                <PersonRoundedIcon sx={{ fontSize: 20 }} />
                              </Avatar>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '15px',
                                    lineHeight: '22px',
                                    color: isRowEnabled ? ACTIVE_SECTION_ICON_COLOR : '#334155',
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  <ClientNameLink clientId={row.id} className="text-inherit hover:text-inherit">
                                    {row.fullName}
                                  </ClientNameLink>
                                </Typography>
                                {row.conditionsText ? (
                                  <Typography
                                    sx={{
                                      mt: 0.25,
                                      fontFamily: 'Arial, sans-serif',
                                      fontSize: '14px',
                                      lineHeight: '20px',
                                      color: '#667085',
                                      overflowWrap: 'anywhere',
                                    }}
                                  >
                                    {row.conditionsText}
                                  </Typography>
                                ) : null}
                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    color: '#98a2b3',
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {row.activityLabel}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <TextField
                              data-action-tour-id="questionnaire-assignment-deadline"
                              type="date"
                              size="small"
                              disabled={!isRowEnabled}
                              value={currentState.deadline}
                              onChange={(event) =>
                                updateRowState(row.id, (draft) => ({
                                  ...draft,
                                  deadline: event.target.value,
                                }))
                              }
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ min: minimumDeadlineDate }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '14px',
                                  bgcolor: '#fbfdff',
                                  '& fieldset': { borderColor: '#e4ebf2' },
                                },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              data-action-tour-id="questionnaire-assignment-message"
                              size="small"
                              disabled={!isRowEnabled}
                              value={currentState.message}
                              onChange={(event) =>
                                updateRowState(row.id, (draft) => ({
                                  ...draft,
                                  message: event.target.value,
                                }))
                              }
                              placeholder="Add a personal note"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '14px',
                                  bgcolor: '#fbfdff',
                                  '& fieldset': { borderColor: '#e4ebf2' },
                                },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.25}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    data-action-tour-id="questionnaire-assignment-delivery"
                                    disabled={!isRowEnabled}
                                    checked={currentState.methods.email}
                                    onChange={(event) =>
                                      updateRowState(row.id, (draft) => ({
                                        ...draft,
                                        methods: {
                                          ...draft.methods,
                                          email: event.target.checked,
                                        },
                                      }))
                                    }
                                    size="small"
                                    sx={{
                                      color: '#98a2b3',
                                      '&.Mui-checked': {
                                        color: ACTIVE_SECTION_ICON_COLOR,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                      sx={{
                                        ...(currentState.methods.email
                                          ? selectedDeliveryTextSx
                                          : {
                                              fontFamily: 'Arial, sans-serif',
                                              fontSize: '14px',
                                              color: '#667085',
                                            }),
                                      }}
                                    >
                                      Email
                                    </Typography>
                                  </Box>
                                }
                                sx={{ m: 0 }}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!clientsLoadFailed && visibleRows.length > 0 && (
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.75,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  borderTop: '1px solid #edf2f7',
                  bgcolor: '#fbfdff',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#667085',
                  }}
                >
                  Showing {visibleRows.length} of {clientsTotal} clients
                </Typography>
                {hasNextPage ? (
                  <Button
                    variant="outlined"
                    disabled={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                    sx={{
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      minWidth: { xs: '100%', sm: 170 },
                      height: { xs: 44, sm: 40 },
                      borderRadius: '12px',
                      borderColor: '#dbe6eb',
                      color: '#344054',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#c4d5e0',
                        bgcolor: alpha('#25a8da', 0.03),
                      },
                    }}
                  >
                    {isFetchingNextPage ? 'Loading more...' : 'Load More Clients'}
                  </Button>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '13px',
                      lineHeight: '18px',
                      color: '#98a2b3',
                    }}
                  >
                    All matching clients loaded
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                borderTop: '1px solid #edf2f7',
                bgcolor: '#fff',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '15px',
                  lineHeight: '22px',
                  color: selectedCount > 0 ? '#475467' : '#667085',
                }}
              >
                {footerMessage}
              </Typography>

              <Button
                data-action-tour-id="questionnaire-assignment-submit"
                variant="contained"
                disabled={clientsLoadFailed || selectedCount === 0 || assignMutation.isPending}
                onClick={handleAssign}
                sx={{
                  minWidth: { xs: '100%', md: 312 },
                  height: 46,
                  borderRadius: '16px',
                  bgcolor: '#20AEDC',
                  color: '#fff',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#199cc7',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#d8eef7',
                    color: '#fff',
                  },
                }}
              >
                {assignMutation.isPending
                  ? 'Assigning...'
                  : `Assign to ${selectedCount} Client${selectedCount === 1 ? '' : 's'}`}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Paper>
      <ActionTour
        ariaLabel="Questionnaire assignment guide"
        label="Assignment guide"
        storageKey="caloriescience:questionnaire-assignment-guide:v1"
        steps={questionnaireAssignmentActionTourSteps}
      />
    </Box>
  );
};

export default QuestionnaireAssignmentPage;
